import { useEffect, useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

/**
 * D-1..D-3: dialog decyzji lub krotkiego zadania.
 *
 * NA ZWYKLYM <div>, NIGDY natywnym <dialog> ani showModal(). Powodem nie jest wyglad,
 * tylko warstwy: natywny dialog laduje w top layer, czyli PONAD wszystkim, co ma
 * z-index. Toast (--z-toast: 60) wyladowalby wtedy POD dialogiem, a W-1 mowi, ze
 * kolejnosc warstw jest nienaruszalna, zas TO-3 stawia toast najwyzej. Pilnuje tego
 * straznik tests/static/noNativeDialogs.test.ts.
 *
 * Wartosci 1:1 z gotowca dialog.html.
 */

/**
 * Elementy, na ktore Tab moze wejsc wewnatrz dialogu.
 *
 * ODSTEPSTWO od gotowca: gotowiec filtruje po `el.offsetParent !== null`. To dziala
 * w przegladarce, ale w jsdom offsetParent jest ZAWSZE null, wiec pulapka fokusu
 * "nie znajdowalaby" niczego i nie dalaby sie przetestowac. Filtrujemy po tym, co
 * realnie wyklucza element z kolejnosci Taba: disabled, hidden i aria-hidden.
 */
function elementyFokusowalne(korzen: HTMLElement): HTMLElement[] {
  const selektor = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  return [...korzen.querySelectorAll<HTMLElement>(selektor)].filter(
    (el) => !el.hasAttribute("disabled") && !el.hidden && el.getAttribute("aria-hidden") !== "true",
  );
}

interface Props {
  otwarty: boolean;
  /** Wywolywane przez Esc, klik w tlo i przycisk zamkniecia. */
  onZamknij: () => void;
  /** D-2: tytul w roli tytulu sekcji. D-4 chce tu pytania z konkretem. */
  tytul: string;
  children?: ReactNode;
  /** D-2: wiersz akcji przy prawej krawedzi, primary skrajnie po prawej. */
  akcje?: ReactNode;
  /**
   * D-4: fokus startowy z JAWNEGO refa, nie z kolejnosci w DOM. Bezpieczny domyslny
   * przycisk ("Anuluj") nie zawsze jest pierwszy w drzewie, a kolejnosc DOM zmienia sie
   * przy kazdej przebudowie stopki.
   */
  poczatkowyFokusRef?: RefObject<HTMLElement | null>;
}

export function Dialog({ otwarty, onZamknij, tytul, children, akcje, poczatkowyFokusRef }: Props): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const wyzwalaczRef = useRef<HTMLElement | null>(null);
  const zamknijRef = useRef(onZamknij);
  const idTytulu = useId();

  zamknijRef.current = onZamknij;

  useEffect(() => {
    if (!otwarty) return;

    // D-3: fokus wraca na wyzwalacz, wiec musimy zapamietac, skad przyszedl.
    wyzwalaczRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.classList.add("modal-open");

    const panel = panelRef.current;
    (poczatkowyFokusRef?.current ?? panel)?.focus();

    function naKlawisz(e: KeyboardEvent): void {
      // Escape zamyka dialog TYLKO wtedy, gdy nikt go wczesniej nie obsluzyl.
      // Rozwiniety Select w srodku dialogu wola preventDefault na swoim Escape,
      // ale nie zatrzymuje propagacji (panel wisi na body, wiec i tak by tu doszlo).
      // Bez tego warunku jedno nacisniecie zamykalo LISTE I CALY DIALOG - klient
      // chcial cofnac rozwiniecie, a tracil caly ekran wyboru.
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault();
        zamknijRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel) return;

      // Pulapka fokusu (A-4): Tab na ostatnim elemencie zawija na pierwszy i odwrotnie.
      const elementy = elementyFokusowalne(panel);
      if (elementy.length === 0) return;
      const pierwszy = elementy[0];
      const ostatni = elementy[elementy.length - 1];
      const aktywny = document.activeElement;

      if (e.shiftKey && (aktywny === pierwszy || aktywny === panel)) {
        e.preventDefault();
        ostatni.focus();
      } else if (!e.shiftKey && aktywny === ostatni) {
        e.preventDefault();
        pierwszy.focus();
      }
    }

    document.addEventListener("keydown", naKlawisz);
    return () => {
      document.removeEventListener("keydown", naKlawisz);
      document.body.classList.remove("modal-open");
      wyzwalaczRef.current?.focus();
    };
  }, [otwarty, poczatkowyFokusRef]);

  return createPortal(
    <div
      className="modal"
      hidden={!otwarty}
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTytulu}
      // D-3: klik w PRZYGASZONE TLO zamyka. Warunek na currentTarget jest istotny -
      // bez niego klik w dowolne miejsce panelu (bo zdarzenie babelkuje) tez by zamykal.
      onClick={(e) => {
        if (e.target === e.currentTarget) onZamknij();
      }}
    >
      <div className="modal__panel" ref={panelRef} tabIndex={-1}>
        <h2 className="modal__title" id={idTytulu}>
          {tytul}
        </h2>
        {children && <div className="modal__body">{children}</div>}
        {akcje && <div className="modal__actions">{akcje}</div>}
      </div>
    </div>,
    document.body,
  );
}
